/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useMemo, useRef, useState } from 'react';
import { Button, Icon } from '@scuf/common';
import './MeterNameCellTheme.scss';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';

interface IMeterNameCellProps {
  meterId: string;
  meterName: string;
  onSubmit: (meterId: string, meterName: string) => Promise<void>;
  isSubmitting: boolean;
  currEditingMeter: string;
  setCurrEditingMeter: (id: string) => void;
}
const getEditMeterIcons = (type: string, size: number = 12): JSX.Element => {
  return <Icon root="common" name={type} exactSize={size} />;
};

function MeterNameCell(props: IMeterNameCellProps) {
  const { meterId, meterName } = props;
  const editIcon = getEditMeterIcons('edit', 18);
  const checkIcon = getEditMeterIcons('check');
  const cancelIcon = getEditMeterIcons('close');
  const [isEditable, setIsEditable] = useState(false);
  const [editDisable, setEditDisable] = useState(false);
  const [newMeterName, setNewMeterName] = useState(meterName);
  const [isEditBtnShown, setIsEditBtnShown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  const maxCharacters = 24;
  const isValid = useMemo(() => {
    if (meterName === newMeterName) return true;
    else {
      const regex = /^[a-zA-Z0-9][a-zA-Z0-9\s\_]*[a-zA-Z0-9]$/;
      return regex.test(newMeterName);
    }
  }, [newMeterName]);

  // For Silent Refresh, not working
  // useEffect(() => {
  //   if (props.meterName !== newMeterName) {
  //     setNewMeterName(props.meterName);
  //   }
  // }, [props.meterName]);

  const onHoverHandler = (value: boolean) => {
    if (props.currEditingMeter === '' || props.currEditingMeter === meterId) {
      setIsEditBtnShown(value);
    }
  };
  const onChangeHandler = (evt: React.FormEvent<HTMLInputElement>) => {
    setNewMeterName(evt.currentTarget.value);
  };
  const toggleEdit = (value: boolean) => {
    if (value) {
      inputRef.current?.focus();
    }
    setIsEditable(value);
    props.setCurrEditingMeter(value ? meterId : '');
  };
  const cancelEdit = () => {
    setNewMeterName(meterName);
    toggleEdit(false);
  };
  const submitEdit = async () => {
    setEditDisable(true);
    let submittingmetername = newMeterName.trim();
    await props.onSubmit(meterId, submittingmetername);
    setEditDisable(false);
    toggleEdit(false);
  };
  return (
    <div
      onMouseEnter={() => onHoverHandler(true)}
      onMouseLeave={() => onHoverHandler(false)}
      className={'meter-name-div'}
    >
      <div className={isEditable ? 'editing' : ''}>
        <div className="container">
          <input
            value={newMeterName}
            className={`meter-name-cell ${isValid ? '' : 'error'}`}
            onChange={onChangeHandler}
            readOnly={!isEditable}
            ref={inputRef}
            maxLength={maxCharacters}
            title={
              isEditable
                ? t(d.METERNAMEMAXLENGTH, { maxCh: maxCharacters })
                : ''
            }
          />
          {!isEditable && isEditBtnShown && (
            <Button
              type="link"
              content=""
              icon={editIcon}
              onClick={() => toggleEdit(true)}
              className="edit-button"
              disabled={props.isSubmitting}
            ></Button>
          )}
        </div>
        {isEditable && (
          <div className="action-button-container">
            <div className="meter-input-error-message">
              {!isValid ? t(d.METERNAMECHARACTERS) : ''}
            </div>
            <Button
              onClick={cancelEdit}
              type="inline"
              content=""
              icon={cancelIcon}
              className="action-button"
              disabled={editDisable}
            ></Button>
            <Button
              onClick={submitEdit}
              type="inline"
              content=""
              icon={checkIcon}
              className="action-button"
              disabled={editDisable || !isValid}
              loading={editDisable}
            ></Button>
          </div>
        )}
      </div>
    </div>
  );
}
export default MeterNameCell;
